import type {
  AttachmentCorrelationAbandonedPayload,
  AttachmentCorrelationResolvedPayload,
} from "@bt-mobile/shared-contracts";

import { mobile } from "./mobile";

/**
 * Background-upload correlation bridge (web → RN).
 *
 * When a user backgrounds a file upload while creating a NEW entity (id === 0),
 * there is no real entityId yet to attach the upload to. The web side mints a
 * device-local `correlationId` (see buildWebviewPickerOptions) and RN queues the
 * upload under it. These two events close that loop after the entity is saved:
 *
 *  - {@link resolveAttachmentCorrelation} — the entity saved; here is its real id.
 *  - {@link abandonAttachmentCorrelation} — the entity was never saved (form
 *    cancelled, or save failed); drop the queued uploads.
 *
 * Both call the typed `@bt-mobile/shared-contracts` bridge methods, which are
 * version-gated on the mobile side — feature-detect before calling. No-op
 * outside the mobile WebView or on app versions that predate the methods.
 */

/**
 * Tell RN that the new entity finished saving, so it can attach every queued
 * upload for this correlation to the real entity via EntityDocs.
 *
 * @param payload - The correlation id plus the saved entity's identifiers
 *   (`entityId`, `jobId`, `builderId`) RN needs to finalize the queued uploads.
 */
export function resolveAttachmentCorrelation(
  payload: AttachmentCorrelationResolvedPayload,
): void {
  if (!mobile.isWebview) return;
  mobile.attachmentCorrelationResolved?.(payload);
}

/**
 * Tell RN to drop the queued uploads for this correlation because the entity
 * will never exist — the create form was cancelled/closed, or the save failed.
 * Without this, queued uploads keyed to a correlationId that never resolves
 * would leak on the device (RN's own TTL sweep is the backstop, not the
 * primary cleanup).
 *
 * @param payload - The correlation id plus the `jobId`/`builderId` scoping the
 *   queued uploads RN should drop.
 */
export function abandonAttachmentCorrelation(
  payload: AttachmentCorrelationAbandonedPayload,
): void {
  if (!mobile.isWebview) return;
  mobile.attachmentCorrelationAbandoned?.(payload);
}
